import { Browser } from "@capacitor/browser";
import { App } from "@capacitor/app";
import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import type { PluginListenerHandle } from "@capacitor/core";

const REDIRECT_URI = "hr.vomi.b2b://auth/callback";
const REFRESH_TOKEN_KEY = "kc_refresh_token";

const KC_URL = (import.meta.env.VITE_KEYCLOAK_URL ?? "").replace(/\/$/, "");
const KC_REALM = import.meta.env.VITE_KEYCLOAK_REALM ?? "";
const KC_CLIENT = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "";

export interface NativeTokenResponse {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  expires_in?: number;
}

function oidcBase(): string {
  return `${KC_URL}/realms/${KC_REALM}/protocol/openid-connect`;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLength: number): string {
  const arr = new Uint8Array(byteLength);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

function openBrowserAndWaitForCode(authUrl: string, expectedState: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const subs: PluginListenerHandle[] = [];
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      subs.forEach((s) => s.remove());
      fn();
    };
    // appUrlOpen fan-outi svim registriranim listenerima — ako neki drugi (stariji, tuđi)
    // callback stigne na ovaj listener, ignoriramo ga umjesto da rušimo cijeli flow.
    // Bez ovog timeouta bi to ignoriranje moglo ostaviti promise trajno pending.
    const timeoutId = setTimeout(
      () => settle(() => reject(new Error("Prijava je istekla, pokušajte ponovno."))),
      120_000,
    );

    Promise.all([
      App.addListener("appUrlOpen", ({ url }) => {
        if (!url.startsWith(REDIRECT_URI)) return;
        try {
          const params = new URL(url).searchParams;
          if (params.get("state") !== expectedState) return;
          settle(() => {
            const error = params.get("error");
            if (error) return reject(new Error(`Keycloak vratio grešku: ${error}`));
            const code = params.get("code");
            if (!code) return reject(new Error("Nedostaje authorization code."));
            resolve(code);
          });
          void Browser.close().catch(() => undefined);
        } catch (e) {
          settle(() => reject(e as Error));
        }
      }),
      // Zatvaranje browsera bez dovršene prijave — inače promise visi zauvijek.
      Browser.addListener("browserFinished", () =>
        settle(() => reject(new Error("Prijava prekinuta."))),
      ),
    ])
      .then((handles) => {
        subs.push(...handles);
        return Browser.open({ url: authUrl, toolbarColor: "#FFFFFF" });
      })
      .catch((e) => settle(() => reject(e as Error)));
  });
}

async function exchangeCodeForTokens(code: string, verifier: string): Promise<NativeTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: KC_CLIENT,
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });
  const res = await fetch(`${oidcBase()}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) throw new Error(`Razmjena koda nije uspjela (HTTP ${res.status}).`);
  return (await res.json()) as NativeTokenResponse;
}

let inFlight: Promise<NativeTokenResponse> | null = null;

// Singleton guard: bez ovoga bi dva istovremena poziva (npr. retry gumb dok je stari
// pokušaj još živ) registrirala dva appUrlOpen listenera s različitim expectedState —
// jedan redirect bi dostavljen objema, i onaj čiji state ne odgovara je prije rušio flow.
export function nativeLogin(): Promise<NativeTokenResponse> {
  if (!inFlight) {
    inFlight = doNativeLogin().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function doNativeLogin(): Promise<NativeTokenResponse> {
  const verifier = randomString(48);
  const challenge = await pkceChallenge(verifier);
  const state = randomString(16);

  const authUrl =
    `${oidcBase()}/auth?` +
    new URLSearchParams({
      client_id: KC_CLIENT,
      redirect_uri: REDIRECT_URI,
      response_type: "code",
      // offline_access: refresh token vezan uz 30-dnevni offline idle prozor (Keycloak realm
      // config) umjesto 30-minutnog SSO idle prozora — bez ovoga svaka pauza >30min forsira
      // puni re-login kroz ovaj isti (native browser) flow.
      scope: "openid offline_access",
      state,
      code_challenge: challenge,
      code_challenge_method: "S256",
      prompt: "login",
    }).toString();

  const code = await openBrowserAndWaitForCode(authUrl, state);
  return exchangeCodeForTokens(code, verifier);
}

export async function nativeLogout(): Promise<void> {
  await clearStoredTokens();
}

// Distinguishes wrong-credentials / OTP-required / other Keycloak-reported failures so the login
// screen can show the right message (and, for OTP, point at the PKCE browser fallback which handles
// it natively) instead of one generic error for every 4xx.
export class DirectGrantError extends Error {
  constructor(
    public status: number,
    public keycloakError?: string,
    public keycloakDescription?: string,
  ) {
    super(keycloakDescription || keycloakError || `HTTP ${status}`);
  }
}

// Resource Owner Password Credentials (Direct Access Grant) — email/password go straight to Keycloak's
// token endpoint from this app, no system browser involved. Requires directAccessGrantsEnabled on the
// vomi_b2b client (already set, see keycloak-data/b2b-realm.json). Same offline_access scope as the PKCE
// flow above, so the resulting session behaves identically (30-day offline idle window).
export async function nativeDirectLogin(username: string, password: string): Promise<NativeTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "password",
    client_id: KC_CLIENT,
    username,
    password,
    scope: "openid offline_access",
  });
  const res = await fetch(`${oidcBase()}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new DirectGrantError(res.status, err?.error, err?.error_description);
  }
  return (await res.json()) as NativeTokenResponse;
}

// iOS Keychain / Android Keystore-backed encrypted storage (not @capacitor/preferences, which is
// plain unencrypted SharedPreferences/UserDefaults) — this holds a 30-day-lived refresh token, worth
// the extra protection against a rooted/jailbroken device reading it straight off disk.
export async function storeRefreshToken(token: string): Promise<void> {
  await SecureStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export async function getStoredRefreshToken(): Promise<string | null> {
  return await SecureStorage.getItem(REFRESH_TOKEN_KEY);
}

export async function clearStoredTokens(): Promise<void> {
  await SecureStorage.removeItem(REFRESH_TOKEN_KEY);
}
